<template>
  <div class="inline-flex size-11.5">
    <!-- 触发区：业务侧把 MacroTag 主体通过默认插槽传入 -->
    <div ref="triggerRef" class="inline-flex size-full" @contextmenu="handleTriggerContextmenu">
      <slot />
    </div>
  </div>

  <Teleport :to="APP_SCALE_ROOT_SELECTOR">
    <!-- 一级弹层：右键 trigger 后显示（3 个 icon 操作） -->
    <Transition name="macro-menu-main">
      <div
        v-if="mainOpen"
        ref="mainMenuRef"
        class="fixed z-777 flex items-center w-23 h-5.5 rounded-md bg-white"
        :style="mainMenuStyle"
        @click.stop
        @contextmenu.prevent.stop
      >
        <button
          ref="addButtonRef"
          type="button"
          :class="[
            `inline-flex items-center justify-center flex-1`,
            mainOpen && subOpen ? 'text-brand-blue1' : 'text-text-important-default',
            'border-r border-n-light-default',
            'cursor-pointer transition-all',
            'active:text-brand-blue1 hover:text-blue-dark-hover',
          ]"
          @click.stop="() => handleMainMenuClick('add')"
        >
          <Icon icon="Function-Wireframe-Add1" class="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          class="flex-1 inline-flex items-center justify-center cursor-pointer border-r border-n-light-default transition-all active:text-brand-blue1 hover:text-blue-dark-hover"
          @click.stop="() => handleMainMenuClick('edit')"
        >
          <Icon icon="Writing-Wireframe-Edit" class="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          class="flex-1 inline-flex items-center justify-center cursor-pointer transition-all active:text-brand-blue1 hover:text-blue-dark-hover"
          @click.stop="() => handleMainMenuClick('delete')"
        >
          <Icon icon="Function-Wireframe-Delete" class="w-3.5 h-3.5" />
        </button>
      </div>
    </Transition>

    <!-- 二级弹层：仅由一级第一个按钮（add）左键触发 -->
    <Transition name="macro-menu-sub">
      <div
        v-if="mainOpen && subOpen"
        ref="subMenuRef"
        class="fixed z-788 flex items-center min-w-23 w-fit h-5.5 rounded-md bg-white"
        :style="subMenuStyle"
        @click.stop
        @contextmenu.prevent.stop
      >
        <!-- 指向一级第一个按钮的箭头 -->
        <div
          ref="subArrowRef"
          class="absolute w-1.5 h-1.5 rotate-45 bg-white"
          :style="subArrowStyle"
        ></div>
        <button
          type="button"
          class="flex-1 text-s-medium inline-flex items-center justify-center cursor-pointer border-r border-n-light-default transition-all active:text-brand-blue1 hover:text-blue-dark-hover"
          @click.stop="() => handleSubActionClick('key')"
        >
          {{ $t('keys.macro.menu.key') }}
        </button>
        <button
          type="button"
          class="flex-1 text-s-medium inline-flex items-center justify-center cursor-pointer border-r border-n-light-default transition-all active:text-brand-blue1 hover:text-blue-dark-hover"
          @click.stop="() => handleSubActionClick('delay')"
        >
          {{ $t('keys.macro.menu.delay') }}
        </button>
        <button
          type="button"
          class="flex-1 text-s-medium inline-flex items-center justify-center cursor-pointer transition-all active:text-brand-blue1 hover:text-blue-dark-hover"
          @click.stop="() => handleSubActionClick('xy')"
        >
          {{ $t('keys.macro.menu.coordinate') }}
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
  import { Icon } from '@rapoo/element'
  import {
    APP_SCALE_ROOT_SELECTOR,
    getScaleRootFixedBounds,
    viewportToScaleRootPoint,
  } from '@/composables/useZoomScale'

  export type MainActionType = 'add' | 'edit' | 'delete'
  export type SubActionType = 'key' | 'delay' | 'xy'

  interface Props {
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
  })

  const mainGap = 6
  const subGap = 10
  const viewportPadding = 8

  const emits = defineEmits<{
    (e: 'open-change', open: boolean): void
    (e: 'menu-click', type: MainActionType): void
    (e: 'sub-action-click', type: SubActionType): void
  }>()

  const triggerRef = ref<HTMLElement | null>(null)
  const mainMenuRef = ref<HTMLElement | null>(null)
  const subMenuRef = ref<HTMLElement | null>(null)
  const addButtonRef = ref<HTMLElement | null>(null)
  const subArrowRef = ref<HTMLElement | null>(null)

  const mainOpen = ref(false)
  const subOpen = ref(false)

  const mainMenuPos = ref({ top: 0, left: 0 })
  const subMenuPos = ref({ top: 0, left: 0 })
  // 箭头位置单独维护，便于后续改尺寸时自动对齐
  const subArrowLeft = ref(16)
  const subArrowTop = ref(-3)

  const mainMenuStyle = computed(() => ({
    top: `${mainMenuPos.value.top}px`,
    left: `${mainMenuPos.value.left}px`,
  }))

  const subMenuStyle = computed(() => ({
    top: `${subMenuPos.value.top}px`,
    left: `${subMenuPos.value.left}px`,
  }))

  const subArrowStyle = computed(() => ({
    left: `${subArrowLeft.value}px`,
    top: `${subArrowTop.value}px`,
  }))

  const clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value))
  }

  // 计算一级弹层位置：固定在 trigger 下方，水平居中并做缩放层边界保护
  const updateMainPosition = () => {
    if (!triggerRef.value || !mainMenuRef.value) return

    const triggerRect = triggerRef.value.getBoundingClientRect()
    const menuWidth = mainMenuRef.value.offsetWidth
    const menuHeight = mainMenuRef.value.offsetHeight
    const bounds = getScaleRootFixedBounds()
    // getBoundingClientRect 是视口像素；缩放层里的 fixed 用的是该层布局坐标
    const anchor = viewportToScaleRootPoint(
      triggerRect.left + triggerRect.width / 2,
      triggerRect.bottom,
    )

    const top = clamp(
      anchor.y + mainGap,
      viewportPadding,
      bounds.height - menuHeight - viewportPadding,
    )

    const left = clamp(
      anchor.x - menuWidth / 2,
      viewportPadding,
      bounds.width - menuWidth - viewportPadding,
    )

    mainMenuPos.value = { top: Math.round(top), left: Math.round(left) }
  }

  // 计算二级弹层位置：固定在一级第一个按钮下方，left 与一级保持一致
  const updateSubPosition = () => {
    if (!mainMenuRef.value || !subMenuRef.value || !addButtonRef.value) return

    const addRect = addButtonRef.value.getBoundingClientRect()
    const subWidth = subMenuRef.value.offsetWidth
    const subHeight = subMenuRef.value.offsetHeight
    const bounds = getScaleRootFixedBounds()
    const anchor = viewportToScaleRootPoint(addRect.left + addRect.width / 2, addRect.bottom)

    const top = clamp(
      anchor.y + subGap,
      viewportPadding,
      bounds.height - subHeight - viewportPadding,
    )
    const left = mainMenuPos.value.left

    subMenuPos.value = { top: Math.round(top), left: Math.round(left) }

    // 箭头始终指向一级第一个按钮中心，宽高取真实渲染尺寸避免写死偏移
    const arrowWidth = subArrowRef.value?.offsetWidth || 0
    const arrowHeight = subArrowRef.value?.offsetHeight || 0
    const rawArrowLeft = anchor.x - left - arrowWidth / 2

    subArrowLeft.value = Math.round(clamp(rawArrowLeft, 2, Math.max(2, subWidth - arrowWidth - 2)))
    subArrowTop.value = -(arrowHeight / 2)
  }

  // 统一关闭一级/二级弹层
  const closeAll = () => {
    if (!mainOpen.value && !subOpen.value) return
    mainOpen.value = false
    subOpen.value = false
    emits('open-change', false)
  }

  const openMain = async () => {
    if (props.disabled) return
    mainOpen.value = true
    subOpen.value = false
    emits('open-change', true)
    // 等 DOM 渲染后再计算尺寸和定位
    await nextTick()
    updateMainPosition()
  }

  // 右键 trigger 打开一级弹层
  const handleTriggerContextmenu = async (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    await openMain()
  }

  // 一级菜单操作
  const handleMainMenuClick = async (type: MainActionType) => {
    emits('menu-click', type)

    if (type === 'add') {
      subOpen.value = !subOpen.value
      await nextTick()
      if (subOpen.value) {
        updateSubPosition()
      }
    } else {
      closeAll()
    }
  }

  // 二级菜单操作
  const handleSubActionClick = (type: SubActionType) => {
    emits('sub-action-click', type)
    closeAll()
  }

  const isInsideTarget = (target: EventTarget | null) => {
    if (!(target instanceof Node)) return false
    return Boolean(
      triggerRef.value?.contains(target) ||
      mainMenuRef.value?.contains(target) ||
      subMenuRef.value?.contains(target),
    )
  }

  const handlePointerDown = (event: PointerEvent) => {
    if (!mainOpen.value && !subOpen.value) return
    if (!isInsideTarget(event.target)) {
      closeAll()
    }
  }

  const handleGlobalContextmenu = (event: MouseEvent) => {
    if (!mainOpen.value && !subOpen.value) return
    if (!isInsideTarget(event.target)) {
      closeAll()
    }
  }

  const handleWindowChange = () => {
    if (!mainOpen.value) return
    // 窗口变化时实时重算位置，避免弹层漂移
    updateMainPosition()
    if (subOpen.value) {
      updateSubPosition()
    }
  }

  watch(mainOpen, (open) => {
    if (open) {
      // 开启时挂全局监听：点击外部关闭、窗口变化重排
      document.addEventListener('pointerdown', handlePointerDown, true)
      document.addEventListener('contextmenu', handleGlobalContextmenu, true)
      window.addEventListener('resize', handleWindowChange)
      window.addEventListener('scroll', handleWindowChange, true)
      return
    }

    // 关闭时移除全局监听，防止内存泄漏
    document.removeEventListener('pointerdown', handlePointerDown, true)
    document.removeEventListener('contextmenu', handleGlobalContextmenu, true)
    window.removeEventListener('resize', handleWindowChange)
    window.removeEventListener('scroll', handleWindowChange, true)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', handlePointerDown, true)
    document.removeEventListener('contextmenu', handleGlobalContextmenu, true)
    window.removeEventListener('resize', handleWindowChange)
    window.removeEventListener('scroll', handleWindowChange, true)
  })
</script>

<style scoped>
  /* 一级/二级弹层统一开合动画：轻微上移 + 淡入 */
  .macro-menu-main-enter-active,
  .macro-menu-main-leave-active,
  .macro-menu-sub-enter-active,
  .macro-menu-sub-leave-active {
    transition:
      opacity 0.16s ease,
      transform 0.18s ease;
    transform-origin: top center;
    will-change: opacity, transform;
  }

  .macro-menu-main-enter-from,
  .macro-menu-main-leave-to,
  .macro-menu-sub-enter-from,
  .macro-menu-sub-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }

  .macro-menu-main-enter-to,
  .macro-menu-main-leave-from,
  .macro-menu-sub-enter-to,
  .macro-menu-sub-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
</style>
